import type { CallLog, CallLogWithCount } from "@virtual-care/shared";
import { requireEnv } from "@virtual-care/shared";
import { authenticatedFetch } from "../utils/auth";

export class CallLogService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = requireEnv(process.env.VUE_APP_API_BASE_URL, "VUE_APP_API_BASE_URL");
  }

  async getMostRecentWithCount(patientId: string): Promise<CallLogWithCount> {
    const response = await authenticatedFetch(`${this.baseUrl}/api/call-logs/patient/${patientId}/recent`);

    if (!response.ok) {
      throw new Error(`Failed to fetch recent call log: ${response.statusText}`);
    }

    return response.json();
  }

  async getCallLogsByPatient(patientId: string, limit?: number): Promise<CallLog[]> {
    const url = new URL(`${this.baseUrl}/api/call-logs/patient/${patientId}`);
    if (limit) {
      url.searchParams.append("limit", limit.toString());
    }

    const response = await authenticatedFetch(url.toString());

    if (!response.ok) {
      throw new Error(`Failed to fetch call logs: ${response.statusText}`);
    }

    const data = await response.json();
    return data.logs ?? data;
  }

  async getCallLogById(callId: string): Promise<CallLog> {
    const response = await authenticatedFetch(`${this.baseUrl}/api/call-logs/${callId}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch call log: ${response.statusText}`);
    }

    return response.json();
  }

  async getCallLogBySessionId(sessionId: string): Promise<CallLog | null> {
    const response = await authenticatedFetch(`${this.baseUrl}/api/call-logs/session/${sessionId}`);

    if (!response.ok) {
      if (response.status === 404) return null;
      throw new Error(`Failed to fetch call log: ${response.statusText}`);
    }

    return response.json();
  }

  async createCallLog(log: Omit<CallLog, "callId" | "createdAt" | "updatedAt">): Promise<CallLog> {
    const response = await authenticatedFetch(`${this.baseUrl}/api/call-logs`, {
      method: "POST",
      body: JSON.stringify(log),
    });

    if (!response.ok) {
      throw new Error(`Failed to create call log: ${response.statusText}`);
    }

    return response.json();
  }

  async updateCallLog(
    callId: string,
    updates: Partial<Pick<CallLog, "endTime" | "duration" | "status" | "endReason" | "sessionId" | "participants" | "notes" | "notesEditedAt" | "notesEditedBy">>
  ): Promise<CallLog> {
    const response = await authenticatedFetch(`${this.baseUrl}/api/call-logs/${callId}`, {
      method: "PUT",
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      throw new Error(`Failed to update call log: ${response.statusText}`);
    }

    return response.json();
  }
}

export const callLogService = new CallLogService();
